<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';

defineProps({
  hasBottomPadding: {
    type: Boolean,
    default: false,
  },
});

const getters = useStoreGetters();
const globalConfig = computed(() => getters['globalConfig/get'].value);

const leftSlotContainer = ref(null);
const scrollContainer = ref(null);
const showScrollIndicator = ref(false);

// Fractional-pixel tolerance: mobile browsers (especially Android WebView) render
// sub-pixel scroll positions, so a pure integer comparison causes false negatives.
const BOTTOM_THRESHOLD = 20;

let rafId = null;
let pendingCheck = false;
let resizeObserver = null;

const checkScrollable = () => {
  if (!scrollContainer.value) return;
  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
  // +1 guards against 1-pixel sub-pixel layout drift reported on some iOS versions.
  const isScrollable = scrollHeight > clientHeight + 1;
  const isAtBottom =
    Math.ceil(scrollTop + clientHeight) >= scrollHeight - BOTTOM_THRESHOLD;
  showScrollIndicator.value = isScrollable && !isAtBottom;
};

const scheduleCheck = () => {
  if (pendingCheck) return;
  pendingCheck = true;
  rafId = requestAnimationFrame(() => {
    pendingCheck = false;
    checkScrollable();
  });
};

// iOS Safari and Android Chrome both move the visual viewport when the address
// bar collapses or the software keyboard opens.  window.resize either doesn't
// fire or fires before layout settles in those cases, so we must listen to
// visualViewport directly.  Two rAF ticks are needed on iOS because the first
// tick still reflects the pre-transition dimensions.
const handleVisualViewport = () => {
  requestAnimationFrame(() => requestAnimationFrame(scheduleCheck));
};

const cleanup = () => {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  scrollContainer.value?.removeEventListener('scroll', scheduleCheck);
  window.removeEventListener('resize', scheduleCheck);
  window.visualViewport?.removeEventListener('resize', handleVisualViewport);
  window.visualViewport?.removeEventListener('scroll', handleVisualViewport);
};

onMounted(async () => {
  await nextTick();
  if (!scrollContainer.value) return;

  scrollContainer.value.addEventListener('scroll', scheduleCheck, {
    passive: true,
  });

  // ResizeObserver is more reliable than MutationObserver for layout changes:
  // it fires on container resize (rotation, address bar, keyboard) AND on
  // content-height changes (images, validation errors, async slots).
  // Android WebView in particular skips MutationObserver for layout-only reflows.
  resizeObserver = new ResizeObserver(scheduleCheck);
  resizeObserver.observe(scrollContainer.value);
  if (leftSlotContainer.value) {
    resizeObserver.observe(leftSlotContainer.value);
  }

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleVisualViewport);
    window.visualViewport.addEventListener('scroll', handleVisualViewport);
  }

  // Fallback for browsers without visualViewport support.
  window.addEventListener('resize', scheduleCheck);

  scheduleCheck();
  // Secondary check after a short delay covers slow async slot content
  // (font swap, lazy images) that may not yet have affected layout at mount.
  setTimeout(scheduleCheck, 350);
});

onUnmounted(cleanup);
</script>

<template>
  <div
    class="flex flex-col w-full justify-start items-start h-dvh min-h-dvh bg-[#F4F4F4] bg-dot-pattern bg-dots-24 dark:bg-dots-24 dark:!bg-slate-900 dark:bg-dot-pattern-dark min-[960px]:bg-none min-[960px]:bg-transparent dark:min-[960px]:bg-none dark:min-[960px]:bg-transparent px-0 py-0 pb-5 overflow-hidden min-[960px]:justify-center min-[960px]:items-center"
    :class="{ '!pb-[86px] min-[960px]:!pb-0': hasBottomPadding }"
  >
    <img
      :src="globalConfig?.logo"
      class="flex mx-auto w-8 h-10 mt-6 mb-4 min-[960px]:hidden dark:hidden"
    />

    <img
      :src="globalConfig?.logoDark"
      class="hidden mx-auto w-8 h-10 mt-6 mb-4 dark:flex dark:min-[960px]:hidden"
    />

    <!-- Main container with gradient border -->
    <!-- `relative` here is the containing block for the scroll indicator so it
         is anchored to the card, not to the scrollable content inside. -->
    <div
      class="relative flex flex-col w-[336px] h-full max-h-[calc(100dvh-120px)] p-[0px] rounded-2xl mx-auto overflow-hidden min-[960px]:w-[896px] min-[960px]:max-h-[calc(100dvh-80px)] min-[960px]:rounded-[40px] min-[960px]:overflow-visible min-[1440px]:w-[1352px] min-[1440px]:h-[816px]"
    >
      <!-- Inner white container -->
      <div
        ref="scrollContainer"
        class="card-gradient-border flex flex-col w-full h-full rounded-2xl overflow-y-auto min-[960px]:flex-row min-[960px]:rounded-[40px] min-[960px]:overflow-y-hidden border border-transparent"
      >
        <!-- Left side (form or main content) -->
        <div
          ref="leftSlotContainer"
          class="relative flex flex-col flex-1 w-full justify-start items-start px-0 py-0 min-h-full min-[960px]:w-0 min-[960px]:min-w-0 min-[960px]:overflow-y-auto dark:bg-slate-900"
        >
          <slot name="left" />
        </div>

        <!-- Divider (visible from 960px and up) -->
        <div
          class="hidden w-px self-stretch bg-[#BDBDBD] dark:bg-slate-700 min-[960px]:block flex-shrink-0"
        />

        <!-- Right side (visible from 960px and up) -->
        <div
          class="hidden flex-1 justify-center items-center min-[960px]:flex min-[960px]:w-0 min-[960px]:min-w-0"
        >
          <slot name="right" />
        </div>
      </div>

      <!-- Scroll indicator: sits outside the scroll container so `absolute`
           positions relative to the card wrapper, not the scrollable content.
           `pointer-events-none` prevents it from intercepting taps/inputs. -->
      <div
        v-if="showScrollIndicator"
        class="absolute left-1/2 -translate-x-1/2 z-10 pointer-events-none min-[960px]:hidden"
        :class="hasBottomPadding ? 'bottom-6' : 'bottom-6'"
      >
        <svg
          width="44"
          height="44"
          viewBox="0 0 44 44"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="44"
            height="44"
            rx="22"
            fill="#55ADFF"
            fill-opacity="0.8"
          />
          <path
            d="M28.0697 24.4299L21.9997 30.4999L15.9297 24.4299"
            stroke="white"
            stroke-width="1.5"
            stroke-miterlimit="10"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M22 13.5V30.33"
            stroke="white"
            stroke-width="1.5"
            stroke-miterlimit="10"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<style>
.card-gradient-border {
  --card-bg: #fbfbfb;
  border-width: 1px;
  background:
    linear-gradient(var(--card-bg), var(--card-bg)) padding-box,
    linear-gradient(110.07deg, #bf36ff 1.38%, #369eff 99.57%) border-box;
  /* Enables momentum (inertial) scrolling on iOS Safari; without this the
     scroll container feels sticky and scroll events fire unreliably. */
  -webkit-overflow-scrolling: touch;
}

.dark .card-gradient-border {
  --card-bg: #111113;
}
</style>
